<template src="./UserProfile.template.html"></template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'

// Design System Components
import LayoutMain from '@/components/ui/LayoutMain.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Badge from '@/components/ui/Badge.vue'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import Input from '@/components/ui/Input.vue'
import Textarea from '@/components/ui/Textarea.vue'
import PhotoUploadSimple from '@/components/PhotoUploadSimple.vue'

// Icons
import {
  Heart,
  ExternalLink,
  User,
  Camera,
  X,
  ChevronLeft,
  ChevronRight,
  Edit,
  Save,
  Loader2
} from 'lucide-vue-next'

// Stores and i18n
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const authStore = useAuthStore()

// State
const user = ref(null)
const userPhotos = ref([])
const isLiked = ref(false)
const isOnline = ref(false)
const isExploring = ref(false)
const viewerOpen = ref(false)
const currentIndex = ref(0)
const loading = ref(true)
const error = ref(null)
let previousBodyOverflow = ''
let photoViewerScrollLocked = false

// Editing state
const isEditing = ref(false)
const isEditingPhotos = ref(false)
const isSaving = ref(false)
const nameError = ref(null)
const bioError = ref(null)
const playPreferencesError = ref(null)
const fetlifeUsernameError = ref(null)

// Edit form data
const editForm = ref({
  name: '',
  pronouns: '',
  bio: '',
  play_preferences: '',
  fetlife_username: ''
})

// Original data for canceling edits
const originalData = ref({})

// Text content processing (no expandable functionality)

// Computed
const isOwnProfile = computed(() => authStore.user?.id === route.params.id)
const isOnboarding = computed(() => user.value?.onboarding_complete === false)

// Form validation
const isFormValid = computed(() => {
  const nameValid = editForm.value.name && editForm.value.name.trim().length > 0 && !nameError.value
  const bioValid = !bioError.value
  const playPreferencesValid = !playPreferencesError.value
  const fetlifeValid = !fetlifeUsernameError.value
  return nameValid && bioValid && playPreferencesValid && fetlifeValid
})

const mainPhotoUrl = computed(
  () =>
    userPhotos.value.find(p => p.is_thumbnail)?.photo_url ||
    userPhotos.value[0]?.photo_url ||
    user.value?.thumbnail_url ||
    '/default-avatar.jpg'
)

const currentPhoto = computed(() => userPhotos.value[currentIndex.value])

// Real current photo for the uploader (no default-avatar fallback, so the
// upload prompt shows for users who have no photo yet)
const editPhotoUrl = computed(
  () => userPhotos.value[0]?.photo_url || user.value?.thumbnail_url || ''
)

// Text content computed properties
const bioLines = computed(() => {
  if (!user.value?.bio) return []
  return user.value.bio.split('\n\n').filter(p => p.trim())
})

const joyLines = computed(() => {
  if (!user.value?.play_preferences) return []
  return user.value.play_preferences.split('\n\n').filter(p => p.trim())
})

// Methods
const loadUserProfile = async () => {
  try {
    loading.value = true
    const userId = route.params.id

    if (isOwnProfile.value) {
      // If viewing own profile, use userStore profile
      if (!userStore.profile) {
        await userStore.fetchProfile(userId)
      }
      user.value = userStore.profile
      userPhotos.value = userStore.profile?.profile_photos || []
    } else {
      // If viewing another user's profile, fetch it separately
      const { data, error } = await supabase
        .from('profiles')
        .select(
          `
          *,
          profile_photos(
            id,
            user_id,
            photo_url,
            is_thumbnail,
            is_primary,
            order,
            created_at
          )
        `
        )
        .eq('id', userId)
        .single()

      if (error) {
        throw error
      }

      // Sort profile photos by order, then by created_at as fallback
      const sortedPhotos = (data.profile_photos || []).sort((a, b) => {
        if (a.order !== null && b.order !== null) {
          return a.order - b.order
        }
        if (a.order !== null) return -1
        if (b.order !== null) return 1
        return new Date(a.created_at) - new Date(b.created_at)
      })

      user.value = { ...data, profile_photos: sortedPhotos }
      userPhotos.value = sortedPhotos
    }

    // Check if user is liked (if authenticated)
    if (authStore.user && !isOwnProfile.value) {
      await userStore.fetchLikes()
      isLiked.value = userStore.likes.includes(userId)
    }

    // Set status flags based on profile data
    isOnline.value =
      user.value?.last_active &&
      new Date(user.value.last_active) > new Date(Date.now() - 15 * 60 * 1000) // 15 minutes
    isExploring.value =
      user.value?.zin_in_spel &&
      user.value?.zin_in_spel_until &&
      new Date(user.value.zin_in_spel_until) > new Date()
  } catch (err) {
    error.value = 'Failed to load profile'
    console.error(err)
  } finally {
    loading.value = false
  }
}

const toggleLike = async () => {
  try {
    if (!authStore.user) {
      error.value = 'Please log in to like profiles'
      return
    }

    const newState = !isLiked.value
    isLiked.value = newState

    await userStore.toggleLike(user.value.id)

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(50)
    }
  } catch (err) {
    isLiked.value = !isLiked.value
    error.value = 'Failed to update like status'
  }
}

const lockPageScroll = () => {
  if (photoViewerScrollLocked) return

  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  photoViewerScrollLocked = true
}

const unlockPageScroll = () => {
  if (!photoViewerScrollLocked) return

  document.body.style.overflow = previousBodyOverflow
  previousBodyOverflow = ''
  photoViewerScrollLocked = false
}

const cleanupPhotoViewer = () => {
  unlockPageScroll()
  window.removeEventListener('keydown', handleKeyboard)
}

const openPhotoViewer = index => {
  currentIndex.value = index
  viewerOpen.value = true
  lockPageScroll()
  window.addEventListener('keydown', handleKeyboard)
}

const closeViewer = () => {
  viewerOpen.value = false
  cleanupPhotoViewer()
}

const handleKeyboard = e => {
  if (e.key === 'Escape') closeViewer()
  if (e.key === 'ArrowLeft') previousPhoto()
  if (e.key === 'ArrowRight') nextPhoto()
}

const previousPhoto = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const nextPhoto = () => {
  if (currentIndex.value < userPhotos.value.length - 1) {
    currentIndex.value++
  }
}

const goToEdit = () => {
  router.push({
    name: 'user-profile',
    params: { id: route.params.id },
    query: { mode: 'edit' }
  })
}

// Validation functions
const validateName = () => {
  if (!editForm.value.name || editForm.value.name.trim().length === 0) {
    nameError.value = 'Name is required'
    return false
  }
  nameError.value = null
  return true
}

const clearNameError = () => {
  if (nameError.value) {
    nameError.value = null
  }
}

const validateBio = () => {
  if (editForm.value.bio && editForm.value.bio.trim().length > 500) {
    bioError.value = 'Bio must be 500 characters or less'
    return false
  }
  bioError.value = null
  return true
}

const clearBioError = () => {
  if (bioError.value) {
    bioError.value = null
  }
}

const validatePlayPreferences = () => {
  if (editForm.value.play_preferences && editForm.value.play_preferences.trim().length > 500) {
    playPreferencesError.value = 'Play preferences must be 500 characters or less'
    return false
  }
  playPreferencesError.value = null
  return true
}

const clearPlayPreferencesError = () => {
  if (playPreferencesError.value) {
    playPreferencesError.value = null
  }
}

const validateFetLifeUsername = () => {
  const username = editForm.value.fetlife_username?.trim() || ''

  if (!username) {
    fetlifeUsernameError.value = null
    return true
  }

  const pattern = /^[a-zA-Z0-9_-]+$/
  if (!pattern.test(username)) {
    fetlifeUsernameError.value =
      'Username can only contain letters, numbers, underscores, and hyphens'
    return false
  }

  if (username.length < 3) {
    fetlifeUsernameError.value = 'Username must be at least 3 characters'
    return false
  }

  if (username.length > 50) {
    fetlifeUsernameError.value = 'Username must be 50 characters or less'
    return false
  }

  fetlifeUsernameError.value = null
  return true
}

// Edit mode functions
const enterEditMode = async () => {
  if (!isOwnProfile.value) return

  // Store original data for canceling
  originalData.value = {
    name: user.value.name || '',
    pronouns: user.value.pronouns || '',
    bio: user.value.bio || '',
    play_preferences: user.value.play_preferences || '',
    fetlife_username: user.value.fetlife_username || ''
  }

  // Initialize edit form with current data
  editForm.value = { ...originalData.value }
  isEditing.value = true
}

const enterPhotoEditMode = () => {
  if (!isOwnProfile.value) return
  isEditingPhotos.value = true
}

const exitPhotoEditMode = () => {
  isEditingPhotos.value = false
}

const cancelEdit = () => {
  isEditing.value = false
  isEditingPhotos.value = false
  editForm.value = { ...originalData.value }
  error.value = null
  nameError.value = null
  bioError.value = null
  playPreferencesError.value = null
  fetlifeUsernameError.value = null
}

// Refresh profile after a photo upload so the new photo is reflected locally.
// The photo is already persisted (profile_photos row + thumbnail) by the
// uploader; saving the profile afterwards flips onboarding_complete via the
// store's isProfileComplete check.
const handlePhotoUploaded = async () => {
  try {
    await userStore.fetchProfile(user.value.id)
    user.value = { ...user.value, ...userStore.profile }
    userPhotos.value = userStore.profile?.profile_photos || []
  } catch (err) {
    console.error('Error refreshing profile after photo upload:', err)
  }
}

const saveChanges = async () => {
  if (!isOwnProfile.value || isSaving.value) return

  try {
    isSaving.value = true
    error.value = null

    // Validate all fields
    const nameValid = validateName()
    const bioValid = validateBio()
    const playPreferencesValid = validatePlayPreferences()
    const fetlifeValid = validateFetLifeUsername()

    if (!nameValid || !bioValid || !playPreferencesValid || !fetlifeValid) {
      return
    }

    // Prepare update data
    const updates = {
      name: editForm.value.name.trim(),
      pronouns: editForm.value.pronouns?.trim() || null,
      bio: editForm.value.bio?.trim() || null,
      play_preferences: editForm.value.play_preferences?.trim() || null,
      fetlife_username: editForm.value.fetlife_username?.trim() || null,
      updated_at: new Date().toISOString()
    }

    // Update profile using userStore
    await userStore.updateProfile(updates)

    // Update local user data
    Object.assign(user.value, userStore.profile)

    // Exit edit mode
    isEditing.value = false

    // Navigate back to view mode
    router.push({ name: 'user-profile', params: { id: user.value.id } })
  } catch (err) {
    console.error('Error saving profile:', err)
    error.value = err.message || 'Failed to save changes. Please try again.'
  } finally {
    isSaving.value = false
  }
}

// Watch for route changes to handle edit mode
watch(
  () => route.query.mode,
  newMode => {
    if (newMode === 'edit' && isOwnProfile.value && user.value) {
      enterEditMode()
    } else if (!newMode && isEditing.value) {
      // If leaving edit mode, cancel editing
      cancelEdit()
    }
  }
)

// Lifecycle
onUnmounted(() => {
  cleanupPhotoViewer()
})

onMounted(async () => {
  await loadUserProfile()

  // Check if we should enter edit mode based on query parameter
  if (route.query.mode === 'edit' && isOwnProfile.value && user.value) {
    await enterEditMode()
  }
})
</script>

<style scoped>
/* Serene Profile Page Styles */

/* Layout Foundation */
.serene-profile {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 768px;
  margin: 0 auto;
  padding: 0;
}

/* Header */
.serene-header {
  padding: 1rem 0;
}

.back-button {
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.back-button:hover {
  opacity: 1;
}

/* Loading & Error States */
.serene-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
}

.loading-content {
  text-align: center;
}

.loading-text {
  font-size: 1rem;
  color: var(--on-surface-variant);
}

.serene-error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
}

.error-card {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

/* Card System */
.content-card {
  background: var(--surface-container-lowest);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--outline-variant);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.content-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

/* Profile Header */
.profile-header {
  padding: 2rem;
  border: 1px solid var(--outline-variant);
  border-radius: 1rem;
  margin-bottom: 0;
  background: var(--surface-container-lowest);
  box-shadow: var(--shadow-sm);
}

.header-content {
  max-width: 600px;
  margin: 0 auto;
}

.header-main {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

/* Avatar */
.avatar-container {
  position: relative;
  flex-shrink: 0;
}

.profile-avatar {
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid var(--surface-container-high);
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: all 0.4s ease;
}

.profile-avatar:hover {
  transform: scale(1.03);
  box-shadow: var(--shadow-lg);
}

.avatar-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: var(--surface-container-lowest);
  border: 2px solid var(--surface-container-lowest);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-status .status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
}

.avatar-status.exploring .status-dot {
  background-color: var(--secondary);
}

.avatar-status.online .status-dot {
  background-color: var(--primary);
}

/* Header Info */
.header-info {
  flex: 1;
  text-align: left;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary);
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  letter-spacing: 0;
  font-family: var(--font-display);
}

.header-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.pronouns {
  font-size: 1rem;
  color: var(--on-surface-variant);
  font-weight: 500;
  opacity: 1;
  letter-spacing: 0;
}

.status-text {
  font-size: 0.875rem;
  color: var(--on-surface-variant);
  text-transform: lowercase;
  letter-spacing: 0;
  opacity: 1;
}

.status-text.exploring {
  color: var(--secondary);
}

.status-text.online {
  color: var(--primary);
}

.header-edit {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.5;
  color: var(--primary);
}

.header-edit:hover {
  opacity: 0.8;
  transform: scale(1.1);
  background-color: var(--surface-container-high);
}

.edit-icon {
  width: 1.25rem;
  height: 1.25rem;
  transition: all 0.3s ease;
}

.header-save {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.7;
  color: var(--primary);
}

.header-save:hover:not(:disabled) {
  opacity: 1;
  transform: scale(1.1);
  background-color: var(--surface-container-high);
}

.header-save:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.save-icon {
  width: 1.25rem;
  height: 1.25rem;
  transition: all 0.3s ease;
}

.header-cancel {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.5;
  color: var(--secondary);
}

.header-cancel:hover:not(:disabled) {
  opacity: 0.8;
  transform: scale(1.1);
  background-color: var(--surface-container-high);
}

.header-cancel:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.cancel-icon {
  width: 1.25rem;
  height: 1.25rem;
  transition: all 0.3s ease;
}

.header-heart {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.3;
  color: var(--on-surface-variant);
}

.header-heart:hover {
  opacity: 0.6;
  transform: scale(1.1);
  background-color: var(--surface-container-high);
}

.header-heart.is-liked {
  opacity: 0.7;
  color: var(--secondary);
}

.header-heart.is-liked:hover {
  opacity: 1;
}

.heart-icon {
  width: 1.25rem;
  height: 1.25rem;
  transition: all 0.3s ease;
}

/* Content Structure */
.content-header {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary);
  margin: 0;
  letter-spacing: 0;
  font-family: var(--font-display);
}

.photo-count {
  font-size: 0.875rem;
  color: var(--on-surface-variant);
  font-weight: 300;
  opacity: 1;
}

.content-body {
  line-height: 1.8;
  color: var(--on-surface-variant);
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 0;
}

/* Text Content */
.bio-text,
.joy-text {
  /* Simple text display, no expandable functionality */
}

.bio-paragraph,
.joy-paragraph {
  margin: 0 0 1rem 0;
  color: var(--on-surface-variant);
  line-height: 1.8;
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 0;
}

.bio-paragraph:last-child,
.joy-paragraph:last-child {
  margin-bottom: 0;
}

/* Photo Gallery */
.photo-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.gallery-item {
  aspect-ratio: 1;
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s ease;
  position: relative;
}

.gallery-item:hover {
  transform: scale(1.01);
  box-shadow: var(--shadow-md);
}

.gallery-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.gallery-item:hover .gallery-photo {
  transform: scale(1.05);
}

/* Extended Presence */
.presence-description {
  margin: 0 0 1.5rem 0;
  text-align: center;
  color: var(--on-surface-variant);
}

.presence-link {
  text-align: center;
}

.fetlife-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background: var(--primary);
  color: var(--on-primary);
  text-decoration: none;
  border-radius: 0.75rem;
  font-weight: 500;
  text-transform: lowercase;
  letter-spacing: 0;
  transition: all 0.4s ease;
  box-shadow: var(--shadow-md);
}

.fetlife-link:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-lg);
  background: var(--primary-container);
}

/* Photo Viewer */
.photo-viewer {
  position: fixed;
  inset: 0;
  z-index: 50;
  background-color: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  color: rgba(245, 241, 232, 0.7);
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.viewer-close:hover {
  color: var(--surface-container-lowest);
  background-color: rgba(245, 241, 232, 0.1);
  transform: scale(1.05);
}

.viewer-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.viewer-photo {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(26, 26, 26, 0.5);
}

.viewer-nav {
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background-color: rgba(245, 241, 232, 0.1);
  backdrop-filter: blur(16px);
  border-radius: 9999px;
  margin: 0 auto;
  width: fit-content;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(245, 241, 232, 0.2);
}

.nav-btn {
  color: var(--surface-container-lowest);
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.nav-btn:hover:not(:disabled) {
  color: var(--primary-fixed);
  background-color: rgba(245, 241, 232, 0.15);
  transform: scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.photo-counter {
  color: var(--surface-container-lowest);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0 1rem;
}

/* Mobile Responsive */
@media (max-width: 640px) {
  .serene-profile {
    padding: 0 0.75rem;
    gap: 1.5rem;
  }

  .profile-header {
    padding: 1.5rem 0 2rem 0;
  }

  .header-main {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .header-info {
    text-align: center;
  }

  .profile-avatar {
    width: 4rem;
    height: 4rem;
  }

  .profile-name {
    font-size: 1.875rem;
  }

  .header-details {
    gap: 0.75rem;
    justify-content: center;
  }

  .pronouns {
    font-size: 0.9rem;
  }

  .content-card {
    padding: 1.5rem;
  }

  .photo-gallery {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .viewer-close {
    top: 0.75rem;
    right: 0.75rem;
  }

  .viewer-nav {
    bottom: 0.75rem;
    padding: 0.375rem 0.75rem;
  }
}

/* Tablet and up */
@media (min-width: 768px) {
  .serene-profile {
    max-width: 896px;
    gap: 2.5rem;
  }

  .identity-card {
    padding: 3.5rem 2.5rem;
  }

  .content-card {
    padding: 2.5rem;
  }

  .photo-gallery {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
  }
}
</style>
